import styled from '@emotion/styled'
import {
  AutoStack,
  DayPickerStrings,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { StatusText } from 'components/StatusText'
import { Field, FieldArray } from 'formik'
import { FormikDropdown, FormikSpinButton } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import _ from 'lodash'
import { ProvingUserRole, User, UserRole } from 'model'
import {
  DayOfWeek,
  Dropdown,
  IconButton,
  IDropdownOption,
  Label,
  Link,
  Stack,
  Text,
} from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

const StyledSectionTitle = styled.div`
  padding: 15px 0 3px 0;
  border-bottom: 1px solid;
`

export class ResourceRequirementDetailsFormSchema {
  constructor() {
    return Yup.object().shape({
      // resourceRequirementType: Yup.string(),
      orderIdDistribution: Yup.array().when('resourceRequirementType', {
        is: (type) =>
          ['accessCard', 'fuelCard', 'radio', 'testTrack'].includes(type),
        then: Yup.array()
          .of(
            Yup.object().shape({
              orderId: Yup.string().required(),
            })
          )
          .required(),
        otherwise: Yup.array().notRequired(),
      }),
      dateFrom: Yup.date().required(),
      dateTo: Yup.date()
        .when(
          'dateFrom',
          (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
        )
        .required(),
      // testTrack: Yup.string().when('resourceRequirementType', {
      //   is: type => type === 'testTrack',
      //   then: Yup.string().required(),
      //   otherwise: Yup.string().notRequired(),
      // }),
    })
  }
}

export const ResourceRequirementDetailsForm: React.FC = () => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const [selectedType, setSelectedType] = React.useState(
    initialValues.resourceRequirementType
  )
  const pageGeneral = window.location.href.split('?')[0] + '?page=general'
  const pageProvingGround =
    window.location.origin + '/_admin/data/proving-grounds'
  const auth = useAuthContext()
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)

  const resourceRequirementTypeOptions: IDropdownOption[] = [
    { key: 'workstation', text: t('dropdown.option.rrWorkstation') },
    { key: 'liftingPlatform', text: t('dropdown.option.rrLiftingPlatform') },
    {
      key: 'groundWorkStation',
      text: t('dropdown.option.rrGroundWorkStation'),
    },
    { key: 'coldCell', text: t('dropdown.option.rrColdCell') },
    { key: 'testTrack', text: t('dropdown.option.rrTestTrack') },
    { key: 'parkingSpace', text: t('dropdown.option.rrParkingSpace') },
    { key: 'accessCard', text: t('dropdown.option.rrAccessCard') },
    { key: 'fuelCard', text: t('dropdown.option.rrFuelCard') },
    { key: 'radio', text: t('dropdown.option.rrRadio') },
    { key: 'fuel', text: t('dropdown.option.rrFuel') },
    {
      key: 'chargingInfrastructure',
      text: t('dropdown.option.rrChargingInfrastructure'),
    },
  ]

  const orderIds = proving.orderIdDistribution!.map((oId: any) => oId.orderId)
  const orderIdOptions = [
    { key: '', text: 'Keine Auftragsnummer' },
    ...orderIds.map(
      (orderId: any) =>
        ({
          key: orderId,
          text: orderId,
        } as IDropdownOption)
    ),
  ]
  const orderIdOptionsNoEmpty = orderIds.map(
    (orderId: any) =>
      ({
        key: orderId,
        text: orderId,
      } as IDropdownOption)
  )
  const percentages: IDropdownOption[] = []
  _.times(10, (i) => {
    percentages.push({ key: (10 - i) * 10, text: `${(10 - i) * 10} %` })
  })
  const canUpdateCurrentProving = canUpdateProving(proving)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const userCanUpdate = () => {
    if (isMaintenanceHead) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }

  return (
    <FormStack>
      <StyledSectionTitle>
        <Text variant="xLarge" as="h2">
          {t('field.provingTitle.label')}
        </Text>
      </StyledSectionTitle>
      <>
        <Stack>
          <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
            <Label>{t('field.provingTitle.label')}:</Label>
            <Box py={1}>
              <Link href={pageGeneral} target="_blank" title="Zur Erprobung">
                {`${proving.title}, ${proving.id}`}
              </Link>
            </Box>
          </Stack>
          <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
            <Label>{t('field.provingGroundTitle.label')}:</Label>
            <Box py={1}>
              {isAdmin || isResourceManager ? (
                <Link
                  href={pageProvingGround}
                  target="_blank"
                  title="Zum Standort"
                  onClick={() => {
                    localStorage.setItem(
                      'currentProvingGround',
                      JSON.stringify(proving.provingGround)
                    )
                  }}
                >
                  {proving.provingGround ? proving.provingGround.title : ''}
                </Link>
              ) : (
                <Text>
                  {proving.provingGround ? proving.provingGround.title : ''}
                </Text>
              )}
            </Box>
          </Stack>
          <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
            <Label>{t('field.provingStatus.label')}:</Label>
            <Box py={1}>
              <StatusText status={proving!.status || 0} />
            </Box>
          </Stack>
        </Stack>

        <Stack.Item className="composite-field">
          <Label>{t('field.responsible.label')}</Label>
          <Box py={1}>
            <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
              {proving.responsible && proving.responsible.length > 0
                ? proving.responsible.map((user: User, i: any) => {
                    return <Text key={i}>{user.name}</Text>
                  })
                : ''}
            </AutoStack>
          </Box>
        </Stack.Item>
      </>
      <StyledSectionTitle>
        <Text variant="xLarge" as="h2">
          {t('field.resourceRequirementBooking.label')}
        </Text>
      </StyledSectionTitle>
      <>
        <FieldStack>
          <FieldStackItem
            name={'resourceRequirementType'}
            render={(fieldProps) => (
              <FormikDropdown
                label={'Typ'}
                required={true}
                options={resourceRequirementTypeOptions}
                onChanged={(option) => {
                  setSelectedType(option.key)
                }}
                disabled={true}
                {...fieldProps}
              />
            )}
          />
          {selectedType && selectedType !== 'testTrack' && (
            <FieldStackItem
              name={'amount'}
              render={(fieldProps) => (
                <FormikSpinButton
                  label={
                    selectedType && selectedType !== 'testTrack'
                      ? 'Anzahl'
                      : 'Stunden'
                  }
                  labelPosition={Position.top}
                  min={1}
                  max={30}
                  step={1}
                  {...fieldProps}
                  disabled={!userCanUpdate()}
                />
              )}
            />
          )}
          <FieldStackItem
            name={'dateFrom'}
            render={(fieldProps) => (
              <FormikGraphQLDatePicker
                label={'Von'}
                defaultValue={proving.dateFrom}
                // minDate={new Date(proving.dateFrom)}
                // maxDate={new Date(proving.dateTo)}
                firstDayOfWeek={DayOfWeek.Monday}
                strings={DayPickerStrings}
                showWeekNumbers={true}
                formatDate={onFormatDate as any}
                {...fieldProps}
                disabled={!userCanUpdate()}
                initialPickerDate={new Date(proving.dateFrom)}
              />
            )}
          />
          <FieldStackItem
            name={'dateTo'}
            render={(fieldProps) => (
              <FormikGraphQLDatePicker
                label={'Bis'}
                defaultValue={proving.dateTo}
                // minDate={new Date(proving.dateFrom)}
                // maxDate={new Date(proving.dateTo)}
                firstDayOfWeek={DayOfWeek.Monday}
                strings={DayPickerStrings}
                showWeekNumbers={true}
                formatDate={onFormatDate as any}
                {...fieldProps}
                disabled={!userCanUpdate()}
                initialPickerDate={new Date(proving.dateFrom)}
              />
            )}
          />
          {proving.provingGround!.racewayHours &&
            selectedType === 'testTrack' && (
              <>
                <FieldStack>
                  <Label>
                    Ungefähre Anzahl durchschnittliche Streckennutzung je
                    Fahrzeug und Tag in Stunden
                  </Label>
                </FieldStack>
                <FieldStack>
                  <FieldStackItem
                    name="racewayHours"
                    label={t('Stunden')}
                    labelPosition={Position.top}
                    min={0}
                    max={24}
                    component={FormikSpinButton}
                    disabled={!userCanUpdate()}
                  />
                </FieldStack>
              </>
            )}
          {['accessCard', 'fuelCard', 'radio'].includes(selectedType) && (
            <FieldStack horizontal={true}>
              <FieldStackItem
                name={'orderIdDistribution[0].orderId'}
                render={(fieldProps) => (
                  <FormikDropdown
                    label={t('field.orderId.label')}
                    required={true}
                    {...fieldProps}
                    options={orderIdOptions}
                    disabled={!userCanUpdate()}
                  />
                )}
              />
            </FieldStack>
          )}{' '}
          {selectedType === 'testTrack' && (
            <>
              <Label required={true}>{'Auftragsnummern'}</Label>
              <FieldArray name="orderIdDistribution">
                {({ form, name, ...helpers }) => {
                  const values: { orderId: string; percentage: number }[] =
                    form.values[name]

                  return (
                    <AutoStack tokens={{ childrenGap: 4 }}>
                      {values.map((x: any, i: any) => (
                        <AutoStack
                          horizontal={true}
                          verticalAlign="center"
                          tokens={{ childrenGap: 8 }}
                          key={i}
                        >
                          <Field
                            name={`orderIdDistribution[${i}].orderId`}
                            render={(props: any) => (
                              <FormikDropdown
                                styles={{ dropdown: { width: 175 } }}
                                placeholders={{
                                  default: t('section.orderIds.field.orderId'),
                                }}
                                options={
                                  i !== 0
                                    ? orderIdOptions
                                    : orderIdOptionsNoEmpty
                                }
                                {...props}
                                disabled={!userCanUpdate()}
                              />
                            )}
                          />
                          <Field
                            name={`orderIdDistribution[${i}].percentage`}
                            render={(props: any) => (
                              <FormikSpinButton
                                min={0}
                                label="%"
                                labelPosition={Position.end}
                                max={100}
                                onValidate={(value: string) => {
                                  if (
                                    Number(value) > 100 ||
                                    Number(value) < 0 ||
                                    value.trim().length === 0 ||
                                    isNaN(+value)
                                  ) {
                                    if (
                                      props.form.initialValues
                                        .orderIdDistribution &&
                                      props.form.initialValues
                                        .orderIdDistribution[i]
                                    ) {
                                      return Number(
                                        props.form.initialValues
                                          .orderIdDistribution[i].percentage
                                      )
                                    } else {
                                      return 0
                                    }
                                  } else {
                                    return Number(value)
                                  }
                                }}
                                step={5}
                                {...props}
                                disabled={!userCanUpdate()}
                              />
                            )}
                          />
                          <IconButton
                            styles={{ root: { color: 'red' } }}
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => helpers.remove(i)}
                            disabled={!userCanUpdate()}
                          />
                        </AutoStack>
                      ))}
                      <Dropdown
                        styles={{ dropdown: { width: 175 } }}
                        options={orderIdOptions}
                        onChanged={(option) => {
                          if (option.key) {
                            helpers.push({
                              orderId: option.key,
                              percentage:
                                100 -
                                values.reduce(
                                  (sum, x) => sum + x.percentage,
                                  0
                                ),
                            })
                          }
                        }}
                        placeholder={'+ ' + t('section.orderIds.field.orderId')}
                        selectedKey={null}
                        disabled={!userCanUpdate()}
                      />
                      <AutoStack
                        horizontal={true}
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                      >
                        <Box
                          width="175px"
                          style={{ textAlign: 'right' }}
                          px={1}
                        >
                          {t('section.orderIds.field.sum')}
                        </Box>
                        <Box px={1}>
                          {values.reduce((sum, x) => sum + x.percentage, 0)}%
                        </Box>
                      </AutoStack>
                    </AutoStack>
                  )
                }}
              </FieldArray>
            </>
          )}
        </FieldStack>
      </>
    </FormStack>
  )
}
